/**
 * Debug Logger - Environment-aware logging utility
 * Only active in development and staging environments
 */

const isDevelopment = import.meta.env.VITE_ENV === 'development' || 
                      import.meta.env.VITE_ENV === 'staging';

class DebugLogger {
  constructor() {
    this.logs = [];
    this.maxLogs = 1000;
    this.categories = new Set();
    this.enabled = isDevelopment;
  }

  /**
   * Log a debug message with category and data
   * @param {string} category - Category for filtering (e.g., 'CALC_ACCOUNT', 'API', 'CONTEXT')
   * @param {string} message - Human-readable message
   * @param {object} data - Additional data to log
   */
  log(category, message, data = {}) {
    if (!this.enabled) return;
    
    const entry = {
      timestamp: new Date().toISOString(),
      time: new Date().toLocaleTimeString(),
      category,
      message,
      data: this._sanitizeData(data),
      stack: this._getCallerStack()
    };
    
    this.logs.push(entry);
    this.categories.add(category);
    
    // Maintain max log size
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
    
    // Console output with styling
    console.log(
      `%c[${category}]%c ${message}`,
      'color: #2196F3; font-weight: bold',
      'color: inherit',
      data
    );
  }

  /**
   * Log an error with stack trace
   */
  error(category, message, error) {
    if (!this.enabled) return;
    
    const entry = {
      timestamp: new Date().toISOString(),
      time: new Date().toLocaleTimeString(),
      category,
      message,
      error: {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      },
      level: 'error'
    };
    
    this.logs.push(entry);
    this.categories.add(category);
    
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
    
    console.error(`[${category}] ${message}`, error);
  }

  /**
   * Log a warning
   */
  warn(category, message, data = {}) {
    if (!this.enabled) return;
    
    const entry = {
      timestamp: new Date().toISOString(),
      time: new Date().toLocaleTimeString(),
      category,
      message,
      data: this._sanitizeData(data),
      level: 'warn'
    };
    
    this.logs.push(entry);
    this.categories.add(category);
    
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
    
    console.warn(`[${category}] ${message}`, data);
  }

  /**
   * Get logs, optionally filtered by category
   * @param {string|null} category - Category to filter by
   * @returns {Array} Filtered logs
   */
  getLogs(category = null) {
    if (category) {
      return this.logs.filter(l => l.category === category);
    }
    return [...this.logs];
  }

  /**
   * Get all unique categories
   */
  getCategories() {
    return Array.from(this.categories).sort();
  }

  /**
   * Clear all logs
   */
  clear() {
    this.logs = [];
    this.categories.clear();
    console.clear();
  }

  /**
   * Export logs as JSON file
   */
  exportLogs() {
    const exportData = {
      exportedAt: new Date().toISOString(),
      environment: import.meta.env.VITE_ENV,
      totalLogs: this.logs.length,
      categories: this.getCategories(),
      logs: this.logs
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], 
                          { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-logs-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Get statistics about logs
   */
  getStats() {
    const stats = {
      total: this.logs.length,
      byCategory: {},
      byLevel: {
        info: 0,
        warn: 0,
        error: 0
      }
    };
    
    this.logs.forEach(log => {
      // Count by category
      stats.byCategory[log.category] = (stats.byCategory[log.category] || 0) + 1;
      
      // Count by level
      const level = log.level || 'info';
      stats.byLevel[level]++;
    });
    
    return stats;
  }

  /**
   * Sanitize data to prevent circular references
   */
  _sanitizeData(data) {
    try {
      // Try to stringify and parse to catch circular references
      JSON.stringify(data);
      return data;
    } catch (e) {
      // If circular reference, return a simplified version
      return { _error: 'Circular reference detected', _type: typeof data };
    }
  }

  /**
   * Get caller stack trace (simplified)
   */
  _getCallerStack() {
    try {
      const stack = new Error().stack;
      const lines = stack.split('\n');
      // Return the 4th line (caller of log method)
      return lines[4]?.trim() || '';
    } catch (e) {
      return '';
    }
  }
}

// Create singleton instance
export const debugLogger = new DebugLogger();

// Expose to window for console access in dev/staging
if (isDevelopment) {
  window.debugLogger = debugLogger;
  console.log(
    '%c🐛 Debug Logger Active',
    'color: #4CAF50; font-size: 14px; font-weight: bold'
  );
  console.log('Access via window.debugLogger or import from Helpers/debugLogger.js');
  console.log('Methods: log(), error(), warn(), getLogs(), exportLogs(), clear(), getStats()');
}

export default debugLogger;
